import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

interface ZoomContextValue {
  zoom: number;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
}

const STORAGE_KEY = 'malpedia-zoom';
const DEFAULT_ZOOM = 1;
const ZOOM_STEPS = [0.5, 0.67, 0.75, 0.8, 0.9, 1.0, 1.1, 1.25, 1.5, 1.75, 2.0] as const;

const ZoomContext = createContext<ZoomContextValue | null>(null);

function nearestStepIndex(value: number): number {
  let bestIdx = ZOOM_STEPS.indexOf(1.0);
  let bestDelta = Infinity;
  for (let i = 0; i < ZOOM_STEPS.length; i++) {
    const d = Math.abs(ZOOM_STEPS[i] - value);
    if (d < bestDelta) {
      bestDelta = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function loadStoredZoom(): number {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return DEFAULT_ZOOM;
  const parsed = parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_ZOOM;
  return ZOOM_STEPS[nearestStepIndex(parsed)];
}

export function ZoomProvider({ children }: { children: React.ReactNode }) {
  const [zoom, setZoomState] = useState<number>(() => loadStoredZoom());

  const setZoom = useCallback((next: number) => {
    const clamped = ZOOM_STEPS[nearestStepIndex(next)];
    localStorage.setItem(STORAGE_KEY, String(clamped));
    setZoomState(clamped);
  }, []);

  const zoomIn = useCallback(() => {
    setZoomState((prev) => {
      const idx = nearestStepIndex(prev);
      const next = ZOOM_STEPS[Math.min(idx + 1, ZOOM_STEPS.length - 1)];
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomState((prev) => {
      const idx = nearestStepIndex(prev);
      const next = ZOOM_STEPS[Math.max(idx - 1, 0)];
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, String(DEFAULT_ZOOM));
    setZoomState(DEFAULT_ZOOM);
  }, []);

  // Apply zoom to <html>. Uses the CSS `zoom` property (now supported in all
  // modern browsers) so layout boxes scale rather than just visual transform.
  useEffect(() => {
    const root = document.documentElement as HTMLElement & { style: CSSStyleDeclaration };
    root.style.zoom = String(zoom);
    return () => {
      root.style.zoom = '';
    };
  }, [zoom]);

  // Keyboard: Ctrl/Cmd + +/-/0 → zoom in/out/reset.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      // With Ctrl/Cmd held, intercept regardless of focus target — matches
      // native browser zoom behaviour inside form fields.
      switch (e.key) {
        case '+':
        case '=': // same physical key as '+' on US layouts
          e.preventDefault();
          zoomIn();
          break;
        case '-':
        case '_':
          e.preventDefault();
          zoomOut();
          break;
        case '0':
          e.preventDefault();
          reset();
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [zoomIn, zoomOut, reset]);

  // Wheel: Ctrl/Cmd + scroll → step zoom. macOS trackpad pinch synthesizes
  // wheel events with ctrlKey:true, so this also captures pinch-zoom.
  const lastWheelRef = useRef(0);
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const now = performance.now();
      if (now - lastWheelRef.current < 50) return;
      lastWheelRef.current = now;
      if (e.deltaY < 0) zoomIn();
      else if (e.deltaY > 0) zoomOut();
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [zoomIn, zoomOut]);

  return (
    <ZoomContext.Provider value={{ zoom, setZoom, zoomIn, zoomOut, reset }}>
      {children}
    </ZoomContext.Provider>
  );
}

export function useZoom(): ZoomContextValue {
  const ctx = useContext(ZoomContext);
  if (!ctx) {
    throw new Error('useZoom must be used within a ZoomProvider');
  }
  return ctx;
}

export { ZOOM_STEPS };
